// @ts-nocheck
/**
 * Resource Sharing Component for Teachers
 * Share and collaborate on educational resources with other teachers
 */

import React, { useState, useEffect } from 'react';
import {
  Tile,
  Grid,
  Column,
  Button,
  Stack,
  DataTable,
  Tag,
  Modal,
  Form,
  FormGroup,
  TextInput,
  TextArea,
  Select,
  SelectItem,
  Checkbox,
  InlineNotification,
  Loading,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  RadioButton,
  RadioButtonGroup,
  Search,
} from '@carbon/react';

const {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
  TableSelectAll,
  TableSelectRow,
} = DataTable;

import {
  Share,
  UserMultiple,
  Document,
  FolderShared,
  Download,
  View,
  Edit,
  Copy,
  CheckmarkFilled,
  Time,
  Star,
  StarFilled,
  Group,
} from '@carbon/icons-react';

import { api } from '../../services/authService';
import { useAuthStore } from '../../store/authStore';

interface SharedResource {
  share_id: string;
  resource_id: string;
  title: string;
  description: string;
  type: string;
  subject: string;
  grade_level: string;
  shared_by: string;
  shared_by_name: string;
  shared_date: string;
  access_level: 'view' | 'download' | 'edit';
  downloads: number;
  likes: number;
  is_favorite?: boolean;
  tags?: string[];
}

interface Teacher {
  teacher_id: string;
  name: string;
  email: string;
  subjects: string[];
  shared_resources?: number;
}

const ResourceSharing: React.FC = () => {
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [sharedResources, setSharedResources] = useState<SharedResource[]>([]);
  const [mySharedResources, setMySharedResources] = useState<SharedResource[]>([]);
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [selectedResources, setSelectedResources] = useState<string[]>([]);
  const [selectedTeachers, setSelectedTeachers] = useState<string[]>([]);
  const [shareForm, setShareForm] = useState({
    shareNote: '',
    accessLevel: 'view',
    allowModification: false,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSubject, setFilterSubject] = useState('all');
  const [filterGrade, setFilterGrade] = useState('all');

  useEffect(() => {
    fetchSharedResources();
    fetchTeachers();
  }, []);

  const fetchSharedResources = async () => {
    try {
      setLoading(true);

      // Fetch resources shared with me
      const sharedWithMeResponse = await api.get('/v1/teacher/resources/shared-with-me');

      if (sharedWithMeResponse.data?.success) {
        // Process the data but use mock for missing fields
        const processedResources = generateMockSharedResources();
        setSharedResources(processedResources);
      } else {
        // Use mock data as fallback
        setSharedResources(generateMockSharedResources());
      }

      // Mock data for my shared resources
      setMySharedResources(generateMySharedResources());
    } catch (error) {
      console.error('Failed to fetch shared resources:', error);
      // Use mock data as fallback
      setSharedResources(generateMockSharedResources());
      setMySharedResources(generateMySharedResources());
    } finally {
      setLoading(false);
    }
  };

  const generateMockSharedResources = (): SharedResource[] => {
    return [
      {
        share_id: '1',
        resource_id: 'res_1',
        title: 'Interactive Chemistry Lab Experiments',
        description: 'Collection of virtual chemistry experiments with step-by-step guides',
        type: 'lab_guide',
        subject: 'Science',
        grade_level: 'Grade 10-11',
        shared_by: 'teacher_2',
        shared_by_name: 'Dr. Sarah Miller',
        shared_date: new Date(Date.now() - 7 * 86400000).toISOString(),
        access_level: 'download',
        downloads: 45,
        likes: 12,
        is_favorite: true,
        tags: ['chemistry', 'lab', 'interactive'],
      },
      {
        share_id: '2',
        resource_id: 'res_2',
        title: 'Calculus Problem Sets with Solutions',
        description: 'Comprehensive calculus problems with detailed solution guides',
        type: 'worksheet',
        subject: 'Mathematics',
        grade_level: 'Grade 12',
        shared_by: 'teacher_3',
        shared_by_name: 'Prof. John Davis',
        shared_date: new Date(Date.now() - 14 * 86400000).toISOString(),
        access_level: 'view',
        downloads: 28,
        likes: 8,
        is_favorite: false,
        tags: ['calculus', 'mathematics', 'problems'],
      },
      {
        share_id: '3',
        resource_id: 'res_3',
        title: 'World History Timeline Project',
        description: 'Interactive timeline project template for world history classes',
        type: 'project_template',
        subject: 'History',
        grade_level: 'Grade 9-10',
        shared_by: 'teacher_4',
        shared_by_name: 'Ms. Emily Brown',
        shared_date: new Date(Date.now() - 3 * 86400000).toISOString(),
        access_level: 'edit',
        downloads: 67,
        likes: 23,
        is_favorite: true,
        tags: ['history', 'project', 'timeline'],
      },
    ];
  };

  const generateMySharedResources = (): SharedResource[] => {
    return [
      {
        share_id: '4',
        resource_id: 'res_4',
        title: 'Algebra Basics Workbook',
        description: 'Comprehensive workbook covering fundamental algebra concepts',
        type: 'workbook',
        subject: 'Mathematics',
        grade_level: 'Grade 8-9',
        shared_by: user?.user_id || 'current_teacher',
        shared_by_name: 'You',
        shared_date: new Date(Date.now() - 5 * 86400000).toISOString(),
        access_level: 'download',
        downloads: 34,
        likes: 15,
        is_favorite: false,
        tags: ['algebra', 'basics', 'workbook'],
      },
    ];
  };

  const fetchTeachers = async () => {
    try {
      // API call would go here
      // const response = await api.get('/v1/teacher/colleagues');

      // Mock data for now
      setTeachers([
        {
          teacher_id: 'teacher_2',
          name: 'Dr. Sarah Miller',
          email: 'sarah.miller@sigma08.com',
          subjects: ['Science', 'Chemistry'],
          shared_resources: 15,
        },
        {
          teacher_id: 'teacher_3',
          name: 'Prof. John Davis',
          email: 'john.davis@sigma08.com',
          subjects: ['Mathematics', 'Calculus'],
          shared_resources: 8,
        },
        {
          teacher_id: 'teacher_4',
          name: 'Ms. Emily Brown',
          email: 'emily.brown@sigma08.com',
          subjects: ['History', 'Geography'],
          shared_resources: 12,
        },
      ]);
    } catch (error) {
      console.error('Failed to fetch teachers:', error);
    }
  };

  const handleShareResources = async () => {
    try {
      const payload = {
        contentIds: selectedResources,
        shareWithTeachers: selectedTeachers,
        shareType: shareForm.accessLevel,
        shareNote: shareForm.shareNote,
        allowModification: shareForm.allowModification,
      };

      const response = await api.post('/v1/teacher/resources/share', payload);

      if (response.data?.success) {
        setIsShareModalOpen(false);
        setSelectedResources([]);
        setSelectedTeachers([]);
        setShareForm({
          shareNote: '',
          accessLevel: 'view',
          allowModification: false,
        });
        fetchSharedResources();
      }
    } catch (error) {
      console.error('Failed to share resources:', error);
    }
  };

  const toggleFavorite = (shareId: string) => {
    setSharedResources(
      sharedResources.map(resource =>
        resource.share_id === shareId ? { ...resource, is_favorite: !resource.is_favorite } : resource
      )
    );
  };

  const getAccessLevelColor = (level: string) => {
    switch (level) {
      case 'edit':
        return 'green';
      case 'download':
        return 'blue';
      case 'view':
        return 'gray';
      default:
        return 'gray';
    }
  };

  const filteredResources = sharedResources.filter(resource => {
    const matchesSearch =
      resource.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      resource.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      resource.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesSubject = filterSubject === 'all' || resource.subject === filterSubject;
    const matchesGrade = filterGrade === 'all' || resource.grade_level.includes(filterGrade);
    return matchesSearch && matchesSubject && matchesGrade;
  });

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Loading withOverlay={false} />
        <div style={{ marginTop: 16 }}>Loading shared resources...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: '2rem', fontWeight: 400, marginBottom: 8 }}>Resource Sharing Hub</h1>
        <p style={{ color: '#525252' }}>
          Discover, share, and collaborate on educational resources with fellow teachers
        </p>
      </div>

      {/* Statistics */}
      <Grid style={{ marginBottom: 24 }}>
        <Column sm={4} md={2} lg={3}>
          <Tile>
            <div style={{ marginBottom: 8 }}>
              <FolderShared size={24} style={{ color: '#0f62fe' }} />
            </div>
            <div style={{ fontSize: '0.875rem', color: '#525252' }}>Shared With Me</div>
            <div style={{ fontSize: '1.75rem', fontWeight: 600 }}>{sharedResources.length}</div>
          </Tile>
        </Column>
        <Column sm={4} md={2} lg={3}>
          <Tile>
            <div style={{ marginBottom: 8 }}>
              <Share size={24} style={{ color: '#198038' }} />
            </div>
            <div style={{ fontSize: '0.875rem', color: '#525252' }}>My Shares</div>
            <div style={{ fontSize: '1.75rem', fontWeight: 600 }}>{mySharedResources.length}</div>
          </Tile>
        </Column>
        <Column sm={4} md={2} lg={3}>
          <Tile>
            <div style={{ marginBottom: 8 }}>
              <UserMultiple size={24} style={{ color: '#8a3ffc' }} />
            </div>
            <div style={{ fontSize: '0.875rem', color: '#525252' }}>Collaborators</div>
            <div style={{ fontSize: '1.75rem', fontWeight: 600 }}>{teachers.length}</div>
          </Tile>
        </Column>
        <Column sm={4} md={2} lg={3}>
          <Tile>
            <div style={{ marginBottom: 8 }}>
              <Download size={24} style={{ color: '#fa4d56' }} />
            </div>
            <div style={{ fontSize: '0.875rem', color: '#525252' }}>Total Downloads</div>
            <div style={{ fontSize: '1.75rem', fontWeight: 600 }}>
              {sharedResources.reduce((sum, r) => sum + r.downloads, 0)}
            </div>
          </Tile>
        </Column>
      </Grid>

      {/* Action Buttons */}
      <Stack gap={5} style={{ marginBottom: 24 }}>
        <Button kind="primary" renderIcon={Share} onClick={() => setIsShareModalOpen(true)}>
          Share Resources
        </Button>
        <Button kind="secondary" renderIcon={Group}>
          Browse Teachers
        </Button>
        <Button kind="tertiary" renderIcon={Star}>
          My Favorites
        </Button>
      </Stack>

      {/* Resource Tabs */}
      <Tabs>
        <TabList>
          <Tab>Shared With Me</Tab>
          <Tab>My Shared Resources</Tab>
          <Tab>Popular Resources</Tab>
          <Tab>Recent Activity</Tab>
        </TabList>
        <TabPanels>
          <TabPanel>
            {/* Shared With Me Table */}
            <div style={{ marginBottom: 16 }}>
              <Stack gap={4} orientation="horizontal">
                <Search
                  placeholder="Search resources..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  style={{ width: 300 }}
                />
                <Select
                  id="subject-filter"
                  labelText=""
                  value={filterSubject}
                  onChange={e => setFilterSubject(e.target.value)}
                >
                  <SelectItem value="all" text="All Subjects" />
                  <SelectItem value="Mathematics" text="Mathematics" />
                  <SelectItem value="Science" text="Science" />
                  <SelectItem value="English" text="English" />
                  <SelectItem value="History" text="History" />
                </Select>
                <Select
                  id="grade-filter"
                  labelText=""
                  value={filterGrade}
                  onChange={e => setFilterGrade(e.target.value)}
                >
                  <SelectItem value="all" text="All Grades" />
                  <SelectItem value="8" text="Grade 8" />
                  <SelectItem value="9" text="Grade 9" />
                  <SelectItem value="10" text="Grade 10" />
                  <SelectItem value="11" text="Grade 11" />
                  <SelectItem value="12" text="Grade 12" />
                </Select>
              </Stack>
            </div>

            <DataTable
              rows={filteredResources.map(r => ({ ...r, id: r.share_id }))}
              headers={[
                { key: 'title', header: 'Resource' },
                { key: 'shared_by_name', header: 'Shared By' },
                { key: 'subject', header: 'Subject' },
                { key: 'access_level', header: 'Access' },
                { key: 'stats', header: 'Stats' },
                { key: 'actions', header: 'Actions' },
              ]}
              render={({ rows, headers, getTableProps, getHeaderProps, getRowProps }) => (
                <TableContainer>
                  <Table {...getTableProps()}>
                    <TableHead>
                      <TableRow>
                        {headers.map(header => (
                          <TableHeader {...getHeaderProps({ header })} key={header.key}>
                            {header.header}
                          </TableHeader>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((row, i) => {
                        const resource = filteredResources[i];
                        return (
                          <TableRow {...getRowProps({ row })} key={row.id}>
                            <TableCell>
                              <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <Document size={20} />
                                  <div>
                                    <div style={{ fontWeight: 500 }}>{resource.title}</div>
                                    <div style={{ fontSize: '0.875rem', color: '#525252' }}>{resource.description}</div>
                                    <div style={{ marginTop: 4 }}>
                                      {resource.tags?.map(tag => (
                                        <Tag key={tag} size="sm" type="gray" style={{ marginRight: 4 }}>
                                          {tag}
                                        </Tag>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>{resource.shared_by_name}</TableCell>
                            <TableCell>
                              <Tag type="blue">{resource.subject}</Tag>
                              <div style={{ fontSize: '0.75rem', marginTop: 4 }}>{resource.grade_level}</div>
                            </TableCell>
                            <TableCell>
                              <Tag type={getAccessLevelColor(resource.access_level)}>{resource.access_level}</Tag>
                            </TableCell>
                            <TableCell>
                              <div style={{ fontSize: '0.875rem' }}>
                                <div>{resource.downloads} downloads</div>
                                <div>{resource.likes} likes</div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Stack gap={3} orientation="horizontal">
                                <Button kind="ghost" size="sm" renderIcon={View}>
                                  View
                                </Button>
                                {resource.access_level !== 'view' && (
                                  <Button kind="ghost" size="sm" renderIcon={Download}>
                                    Download
                                  </Button>
                                )}
                                <Button
                                  kind="ghost"
                                  size="sm"
                                  hasIconOnly
                                  renderIcon={resource.is_favorite ? StarFilled : Star}
                                  iconDescription="Favorite"
                                  onClick={() => toggleFavorite(resource.share_id)}
                                />
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            />
          </TabPanel>
          <TabPanel>
            <InlineNotification
              kind="info"
              title="My Shared Resources"
              subtitle="View and manage resources you've shared with other teachers"
            />
          </TabPanel>
          <TabPanel>
            <InlineNotification
              kind="info"
              title="Popular Resources"
              subtitle="Discover the most downloaded and liked resources in the community"
            />
          </TabPanel>
          <TabPanel>
            <InlineNotification
              kind="info"
              title="Recent Activity"
              subtitle="See recent shares, downloads, and collaboration activity"
            />
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* Share Resources Modal */}
      <Modal
        open={isShareModalOpen}
        modalHeading="Share Resources"
        primaryButtonText="Share"
        secondaryButtonText="Cancel"
        size="lg"
        onRequestSubmit={handleShareResources}
        onRequestClose={() => setIsShareModalOpen(false)}
      >
        <Form>
          {/* Resource Selection */}
          <FormGroup legendText="Select Resources to Share">
            <InlineNotification
              kind="info"
              subtitle="Select resources from your Content Library to share"
              hideCloseButton
            />
          </FormGroup>

          {/* Teacher Selection */}
          <FormGroup legendText="Share With Teachers">
            {teachers.map(teacher => (
              <Checkbox
                key={teacher.teacher_id}
                id={`teacher-${teacher.teacher_id}`}
                labelText={`${teacher.name} (${teacher.subjects.join(', ')})`}
                checked={selectedTeachers.includes(teacher.teacher_id)}
                onChange={e => {
                  if (e.target.checked) {
                    setSelectedTeachers([...selectedTeachers, teacher.teacher_id]);
                  } else {
                    setSelectedTeachers(selectedTeachers.filter(id => id !== teacher.teacher_id));
                  }
                }}
              />
            ))}
          </FormGroup>

          {/* Access Level */}
          <FormGroup legendText="Access Level">
            <RadioButtonGroup
              name="access-level"
              defaultSelected="view"
              onChange={value => setShareForm({ ...shareForm, accessLevel: value as any })}
            >
              <RadioButton labelText="View Only" value="view" />
              <RadioButton labelText="Download" value="download" />
              <RadioButton labelText="Edit & Modify" value="edit" />
            </RadioButtonGroup>
          </FormGroup>

          {/* Share Note */}
          <FormGroup>
            <TextArea
              id="share-note"
              labelText="Note (Optional)"
              placeholder="Add a note about these resources..."
              rows={3}
              value={shareForm.shareNote}
              onChange={e => setShareForm({ ...shareForm, shareNote: e.target.value })}
            />
          </FormGroup>

          {/* Allow Modification */}
          <FormGroup>
            <Checkbox
              id="allow-modification"
              labelText="Allow recipients to share with others"
              checked={shareForm.allowModification}
              onChange={e => setShareForm({ ...shareForm, allowModification: e.target.checked })}
            />
          </FormGroup>
        </Form>
      </Modal>
    </div>
  );
};

export default ResourceSharing;
